import { DEFAULT_CONTACT_CAPTURE_SETTINGS } from '../../../src/modules/contactCapture/helpers/contactCaptureSettings';

const OVERLAY_OPACITY = 0.56;

/** The page must stay visible behind the popup, so only the hue is configurable. */
export const overlayBackground = (hex: string) => {
  const [r, g, b] = [1, 3, 5].map(start => parseInt(hex.slice(start, start + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${OVERLAY_OPACITY})`;
};

const defaults = DEFAULT_CONTACT_CAPTURE_SETTINGS.styles;

export const WIDGET_STYLES = `
  :host {
    all: initial;
    --bhi-primary: ${defaults.primaryColor};
    --bhi-button: ${defaults.buttonColor};
    --bhi-background: ${defaults.backgroundColor};
    --bhi-text: ${defaults.textColor};
    --bhi-overlay: ${overlayBackground(defaults.overlayColor)};
    --bhi-radius: ${defaults.borderRadius}px;
    display: block;
    width: 100%;
    color: var(--bhi-text);
    font-family: var(--bhi-font, Arial, sans-serif);
    font-size: 16px;
    line-height: 1.4;
    color-scheme: light;
  }

  :host([data-bhi-mode="popup"]) {
    position: fixed;
    inset: 0;
    z-index: 2147483000;
    width: auto;
  }

  :host([data-bhi-mode="inline"]) { height: 100%; }

  *, *::before, *::after { box-sizing: border-box; }
  button, input { font: inherit; }

  .bhi-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow-y: auto;
    padding: 24px;
    background: var(--bhi-overlay);
  }

  /**
   * Auto margins centre the card while it fits and collapse when it does not, so a form taller
   * than the viewport scrolls from its top edge instead of having its ends clipped.
   */
  .bhi-overlay .bhi-card { margin: auto; }

  .bhi-card {
    position: relative;
    width: min(100%, 390px);
    overflow: hidden;
    border: 0;
    border-radius: var(--bhi-radius);
    background: var(--bhi-background);
    color: var(--bhi-text);
    box-shadow: 0 18px 60px rgba(21, 16, 13, 0.28);
  }

  .bhi-inline-frame {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: stretch;
    padding: 0;
  }

  /*
   * Embedded forms have no backdrop or shadow, so an edge separates them from the page. An inset
   * outline takes no space, so the card stays the popup card's size and the frame cannot clip it.
   */
  .bhi-inline-frame .bhi-card {
    width: 100%;
    min-height: 100%;
    outline: 1px solid rgba(0, 0, 0, 0.12);
    outline-offset: -1px;
    box-shadow: none;
  }

  :host([data-bhi-mode="inline"]) .bhi-inline-frame { overflow-y: auto; }
  :host([data-bhi-preview="true"][data-bhi-mode="popup"]) .bhi-card { box-shadow: none; }

  /* In the builder preview the embedded form takes the popup card's size and position. */
  :host([data-bhi-preview="true"][data-bhi-mode="inline"]) .bhi-inline-frame { padding: 24px; }
  :host([data-bhi-preview="true"][data-bhi-mode="inline"]) .bhi-inline-frame .bhi-card {
    width: min(100%, 390px);
    min-height: 0;
    margin: auto;
  }

  .bhi-header {
    padding: 22px 24px 20px;
    background: var(--bhi-primary);
    color: #fff;
  }


  .bhi-brand {
    margin: 0;
    font-size: var(--bhi-brand-size, ${defaults.brandNameSize}px);
    font-weight: var(--bhi-brand-weight, 700);
    font-style: var(--bhi-brand-style, normal);
    letter-spacing: 1.1px;
    text-align: var(--bhi-brand-align, ${defaults.brandNameAlign});
    text-transform: var(--bhi-brand-transform, uppercase);
  }

  .bhi-title {
    margin: 7px 0 0;
    font-size: var(--bhi-title-size, ${defaults.headerTextSize}px);
    font-weight: var(--bhi-title-weight, 700);
    font-style: var(--bhi-title-style, normal);
    text-transform: var(--bhi-title-transform, none);
    line-height: 1.2;
    text-align: var(--bhi-title-align, ${defaults.headerTextAlign});
  }

  /* Header padding is 24px; 28px more clears the 32px close button at right: 10px. */
  .bhi-header-inset { padding-left: 28px; padding-right: 28px; }

  .bhi-close {
    position: absolute;
    z-index: 1;
    top: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.22);
    color: #fff;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
  }

  .bhi-close:hover, .bhi-close:focus-visible { background: rgba(0, 0, 0, 0.36); }

  .bhi-body { padding: 22px 24px 24px; }

  .bhi-description {
    margin: 0 0 18px;
    font-size: 14px;
    line-height: 1.5;
    opacity: 0.8;
  }

  .bhi-form { display: grid; gap: 13px; margin: 0; }
  .bhi-field { display: grid; gap: 6px; }

  .bhi-label {
    font-size: 12px;
    font-weight: 650;
  }

  .bhi-input {
    width: 100%;
    min-height: 42px;
    margin: 0;
    padding: 9px 12px;
    border: 1px solid rgba(67, 54, 47, 0.3);
    border-radius: 8px;
    outline: none;
    background: #fff;
    color: #241b17;
    font-size: 14px;
    box-shadow: none;
  }

  .bhi-input:focus {
    border-color: var(--bhi-primary);
    box-shadow: 0 0 0 3px rgba(111, 78, 55, 0.18);
  }

  .bhi-input-invalid, .bhi-input-invalid:focus {
    border-color: #d5372b;
  }

  .bhi-input-invalid:focus {
    box-shadow: 0 0 0 3px rgba(213, 55, 43, 0.18);
  }

  .bhi-field-error, .bhi-form-error {
    margin: 0;
    color: #a32118;
    font-size: 12px;
    line-height: 1.35;
  }

  .bhi-submit {
    min-height: 44px;
    margin: 2px 0 0;
    padding: 10px 16px;
    border: 0;
    border-radius: 8px;
    background: var(--bhi-button);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
  }

  .bhi-submit:hover, .bhi-submit:focus-visible { filter: brightness(0.94); }
  .bhi-submit:disabled { cursor: wait; opacity: 0.65; }

  .bhi-decline {
    justify-self: center;
    padding: 2px;
    border: 0;
    background: transparent;
    color: inherit;
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
    opacity: 0.68;
  }

  .bhi-consent {
    margin: 0;
    padding: 0 24px 20px;
    font-size: 10px;
    line-height: 1.45;
    opacity: 0.62;
    text-align: center;
  }

  .bhi-status {
    min-height: 220px;
    display: grid;
    place-content: center;
    gap: 12px;
    padding: 32px 24px;
    text-align: center;
  }

  .bhi-status-message { max-width: 310px; margin: 0; font-size: 14px; line-height: 1.5; opacity: 0.8; }

  .bhi-load-error {
    width: min(100%, 520px);
    margin: 8px auto;
    padding: 12px 14px;
    border: 1px solid #e2aaa4;
    border-radius: 8px;
    background: #fff4f2;
    color: #7d1f17;
    font: 13px/1.4 Arial, sans-serif;
  }

  @media (max-width: 520px) {
    .bhi-overlay { align-items: flex-end; padding: 12px; }
    /* Keeps the sheet on the bottom edge, and lets the overlay do the scrolling. */
    .bhi-overlay .bhi-card { width: 100%; margin: auto 0 0; }
    :host([data-bhi-preview="true"][data-bhi-mode="inline"]) .bhi-inline-frame { padding: 12px; }
    :host([data-bhi-preview="true"][data-bhi-mode="inline"]) .bhi-inline-frame .bhi-card { width: 100%; }
    /* The builder frame is not a phone: keep the popup centred where the embedded form sits. */
    :host([data-bhi-preview="true"]) .bhi-overlay .bhi-card { margin: auto 0; }
  }

  @media (prefers-reduced-motion: no-preference) {
    .bhi-card { animation: bhi-enter 180ms ease-out; }
    /* The builder re-renders on every edit, and a replayed entry animation reads as flicker. */
    :host([data-bhi-preview="true"]) .bhi-card { animation: none; }
    @keyframes bhi-enter {
      from { opacity: 0; transform: translateY(8px) scale(0.985); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
  }
`;
